/**
 * OCTOPUS - FASE C2: TelemetryCharts
 * Gráficos de telemetria para a tab Histórico
 * 
 * Extraído e simplificado do EquipmentHistoryPage.js
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer,
  LineChart,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine
} from 'recharts';
import {
  Gauge,
  Activity,
  Fuel,
  Thermometer,
  Zap,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTimezone } from '@/contexts/TimezoneContext';
import {
  buildTimeTicks,
  getLocalMidnights,
  formatDayLabel,
} from './timelineUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// Cores dos gráficos
const COLORS = {
  speed: '#3b82f6',
  port: '#ef4444',      // Vermelho - Bombordo
  starboard: '#22c55e', // Verde - Boreste
  main: '#f59e0b',      // Amarelo - Principal
  engine1: '#eab308',   // Amarelo - Draga
  engine2: '#a855f7',
  engine3: '#14b8a6',
  avg: '#8b5cf6',       // D9 FIX: Roxo - Média
  fuel: '#8b5cf6',
  dragaFuel: '#eab308',
  totalFuel: '#3b82f6',
  total: '#eab308',
  temp: '#f97316',
  voltage: '#06b6d4',
};

const ENGINE_SERIES = [
  { id: 'port', label: 'BB (Bombordo)', color: COLORS.port },
  { id: 'starboard', label: 'BE (Boreste)', color: COLORS.starboard },
  { id: 'main', label: 'Principal', color: COLORS.main },
  { id: 'engine1', label: 'Draga', color: COLORS.engine1 },
  { id: 'engine2', label: 'Motor 2', color: COLORS.engine2 },
  { id: 'engine3', label: 'Motor 3', color: COLORS.engine3 },
];

const FUEL_SERIES = [
  { id: 'port', label: 'BB (Bombordo)', color: COLORS.port },
  { id: 'starboard', label: 'BE (Boreste)', color: COLORS.starboard },
  { id: 'engine1', label: 'Draga', color: COLORS.dragaFuel },
  { id: 'main', label: 'Principal', color: COLORS.main },
];

const FUEL_TOTALIZER_ITEMS = [
  { id: 'port', label: 'BOMBORDO', color: COLORS.port },
  { id: 'starboard', label: 'BORESTE', color: COLORS.starboard },
  { id: 'engine1', label: 'DRAGA', color: COLORS.dragaFuel },
  { id: 'total', label: 'CONSUMO TOTAL', color: COLORS.totalFuel },
];

const ALWAYS_SHOW_FUEL_ENGINE_IDS = ['port', 'starboard', 'engine1'];

const CHART_METRICS = [
  'revolutions',
  'fuel.rate',
  'temperature',
  'coolantTemperature',
  'engineLoad',
  'boostPressure',
  'exhaustTemperature',
];

const TELEMETRY_CHART_PATHS = [
  'navigation.speedOverGround',
  ...ENGINE_SERIES.flatMap(engine => (
    CHART_METRICS.map(metric => `propulsion.${engine.id}.${metric}`)
  )),
];

const CHART_UNIT_FALLBACKS = {
  speed: 'km/h',
  flow: 'L/h',
  temperature: '°C',
  pressure: 'bar',
};

// Piso alinhado à regra canônica de lacuna de telemetria (backend
// FUEL_TELEMETRY_GAP_OFF_S = 300s: sem sinal = motor desligado = zero consumo,
// CC-2026-07-07-005) — a faixa âmbar marca exatamente o que o cálculo zera.
// Em resoluções agregadas o limiar cresce com a grade (2.5× a resolução), pois
// o espaçamento natural das amostras do gráfico é a própria resolução.
// Doc unificada: docs/architecture/TELEMETRY-GAP-RULE.md
const MIN_GAP_THRESHOLD_MINUTES = 5;
const GAP_THRESHOLD_RESOLUTION_MULTIPLIER = 2.5;
const MAX_GAP_REFERENCE_AREAS = 120;

const getGapThresholdMinutes = (meta) => {
  const resolutionSeconds = Number(
    meta?.chart_resolution_seconds ?? meta?.resolution_seconds ?? 0
  );

  if (!Number.isFinite(resolutionSeconds) || resolutionSeconds <= 0) {
    return MIN_GAP_THRESHOLD_MINUTES;
  }

  return Math.max(
    MIN_GAP_THRESHOLD_MINUTES,
    (resolutionSeconds * GAP_THRESHOLD_RESOLUTION_MULTIPLIER) / 60
  );
};

const formatAxisNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
};

const roundUpTo = (value, step) => Math.ceil(value / step) * step;

const buildTicks = (max, step) => {
  const ticks = [];
  for (let value = 0; value <= max; value += step) {
    ticks.push(Number(value.toFixed(2)));
  }
  return ticks;
};

// Passo "redondo" (1/2/2.5/5 × 10^n) para auto-escala de eixo.
const niceStep = (rawStep) => {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const exponent = Math.floor(Math.log10(rawStep));
  const base = Math.pow(10, exponent);
  const fraction = rawStep / base;
  let niceFraction;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 2.5) niceFraction = 2.5;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * base;
};

// Domínio/ticks ajustados ao range dos dados (não força piso em 0) -> abre resolução.
const buildNiceAxis = (range, { targetTicks = 6, padRatio = 0.08, minStep = 0 } = {}) => {
  if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) return null;
  let lo = range.min;
  let hi = range.max;
  if (lo === hi) {
    const bump = Math.abs(lo) > 0 ? Math.abs(lo) * 0.05 : 1;
    lo -= bump;
    hi += bump;
  }
  const pad = (hi - lo) * padRatio;
  lo -= pad;
  hi += pad;
  let step = niceStep((hi - lo) / Math.max(1, targetTicks));
  if (minStep) step = Math.max(step, minStep);
  const niceLo = Math.floor(lo / step) * step;
  const niceHi = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let value = niceLo; value <= niceHi + step / 1000; value += step) {
    ticks.push(Number(value.toFixed(4)));
  }
  if (ticks.length < 2) return null;
  return { domain: [ticks[0], ticks[ticks.length - 1]], ticks };
};

// Helpers de timeline (buildTimeTicks, getLocalMidnights, formatDayLabel) movidos
// para './timelineUtils' e importados acima — compartilhados com a página de PDF.

// Helper para obter headers de autenticação
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Detectar gaps nos dados (períodos sem telemetria)
const detectGaps = (data, gapThresholdMinutes = 10) => {
  const gaps = [];
  const threshold = gapThresholdMinutes * 60 * 1000;
  
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1].time;
    const curr = data[i].time;
    if (curr - prev > threshold) {
      gaps.push({ start: prev, end: curr });
    }
  }
  
  return gaps;
};

const TelemetryCharts = ({
  equipmentId,
  period = '-24h',
  timeRange = null,
  fuelTotalsByMotor = {},
  totalFuelValue = null,
  totalFuelUnit = 'L',
  refreshNonce = 0,
  // --- props opcionais usadas pela página de PDF ---
  selectedCharts = null,   // array de ids p/ filtrar (null = mostra todos disponíveis)
  columns = null,          // 1 ou 2 (null = layout padrão vertical da tela)
  chartHeight = 200,       // altura de cada gráfico em px
  chartWidth = '100%',     // largura: string ('100%') p/ responsivo, number (px) p/ fixo
}) => {
  const isChartSelected = (id) => !selectedCharts || selectedCharts.includes(id);
  const { formatDateTime, timezone } = useTimezone();
  const [chartData, setChartData] = useState([]);
  const [dataGaps, setDataGaps] = useState([]);
  const [displayPaths, setDisplayPaths] = useState({});
  const [, setHistoryMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestRef = useRef(null);
  
  // Fetch telemetria
  const fetchTelemetry = useCallback(async () => {
    if (!equipmentId) return;
    void refreshNonce; // dep usada apenas para forçar re-fetch ao clicar em "Atualizar"

    let controller = null;
    try {
      if (requestRef.current) {
        requestRef.current.abort();
      }
      controller = new AbortController();
      requestRef.current = controller;
      setLoading(true);
      setError(null);
      const resolvedRange = timeRange || { start: period, stop: 'now()', key: period };
      const params = new URLSearchParams({
        start: resolvedRange.start || period,
        paths: TELEMETRY_CHART_PATHS.join(','),
      });
      if (resolvedRange.stop && resolvedRange.stop !== 'now()') {
        params.set('stop', resolvedRange.stop);
      }
      
      const res = await axios.get(
        `${API}/api/equipments/${equipmentId}/signalk/telemetry?${params.toString()}`,
        { headers: getAuthHeaders(), timeout: 120000, signal: controller.signal }
      );
      if (requestRef.current !== controller) {
        return;
      }
      
      const data = res.data.data || [];
      const meta = res.data.meta || null;
      setDisplayPaths(res.data.display?.paths || {});
      setHistoryMeta(meta);
      
      // Processar dados para gráficos — SÓ valores de exibição prontos do
      // backend (point.display / point.computed). Sem fallback em chave legada
      // crua e sem recomputar telemetria (INV-LAYER-READPATH-001,
      // INV-UI-TRAIL-001; ADR-2026-10-25).
      const processed = data.map(point => {
        const time = new Date(point.time || point._time).getTime();
        const getDisplay = (path) => {
          const display = point.display?.[path];
          if (display?.value !== null && display?.value !== undefined) {
            return display.value;
          }
          return null;
        };
        const getEngineDisplay = (engineId, metric, altMetrics = []) => {
          for (const candidateMetric of [metric, ...altMetrics]) {
            const value = getDisplay(`propulsion.${engineId}.${candidateMetric}`);
            if (value !== null) {
              return value;
            }
          }
          return null;
        };
        const buildEngineValues = (engineId) => ({
          [`${engineId}_rpm`]: getEngineDisplay(engineId, 'revolutions'),
          [`${engineId}_fuel`]: getEngineDisplay(engineId, 'fuel.rate'),
          [`${engineId}_temp`]: getEngineDisplay(engineId, 'temperature', ['coolantTemperature']),
          [`${engineId}_load`]: getEngineDisplay(engineId, 'engineLoad'),
          [`${engineId}_boost`]: getEngineDisplay(engineId, 'boostPressure'),
          [`${engineId}_exhaust_temp`]: getEngineDisplay(engineId, 'exhaustTemperature'),
        });

        const engineValues = ENGINE_SERIES.reduce((values, engine) => ({
          ...values,
          ...buildEngineValues(engine.id),
        }), {});
        const computed = point.computed || {};

        return {
          time,
          speed_display: getDisplay('navigation.speedOverGround'),
          ...engineValues,
          // D9: totais/médias BB+BE calculados no backend (point.computed).
          avg_rpm: computed.avg_rpm ?? null,
          avg_fuel: computed.avg_fuel ?? null,
          total_fuel: computed.total_fuel ?? null,
          avg_temp: computed.avg_temp ?? null,
        };
      }).filter(d => d.time);
      
      // Ordenar por tempo
      processed.sort((a, b) => a.time - b.time);
      
      setChartData(processed);
      setDataGaps(detectGaps(processed, getGapThresholdMinutes(meta)));
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Erro ao buscar telemetria:', err);
      setError('Não foi possível carregar os dados de telemetria');
    } finally {
      if (controller && requestRef.current === controller && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [equipmentId, period, timeRange, refreshNonce]);

  useEffect(() => {
    fetchTelemetry();
    return () => {
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchTelemetry]);
  
  // Verificar se há dados para cada tipo de gráfico
  const hasSpeed = chartData.some(d => d.speed_display != null);
  const hasEngineMetric = (metric) => chartData.some(d => (
    ENGINE_SERIES.some(engine => d[`${engine.id}_${metric}`] != null)
  ));
  const hasRpm = hasEngineMetric('rpm');
  const hasFuel = hasEngineMetric('fuel');
  const hasTemp = hasEngineMetric('temp');
  const hasLoad = hasEngineMetric('load');
  const hasBoost = hasEngineMetric('boost');
  const hasExhaustTemp = hasEngineMetric('exhaust_temp');
  const getDisplayUnit = (path, fallback) => displayPaths[path]?.unit || fallback;
  const speedLabel = getDisplayUnit('navigation.speedOverGround', CHART_UNIT_FALLBACKS.speed);
  const flowLabel = getDisplayUnit('propulsion.port.fuel.rate', CHART_UNIT_FALLBACKS.flow);
  const temperatureLabel = getDisplayUnit('propulsion.port.temperature', CHART_UNIT_FALLBACKS.temperature);
  // Rótulo vem do backend ('°F'); 'degF' era o rótulo antigo do UnitsContext.
  const isFahrenheit = temperatureLabel === '°F' || temperatureLabel === 'degF';
  const pressureLabel = getDisplayUnit('propulsion.port.boostPressure', CHART_UNIT_FALLBACKS.pressure);
  const getMetricMax = (metric) => Math.max(
    0,
    ...chartData.flatMap(point => (
      ENGINE_SERIES
        .map(engine => point[`${engine.id}_${metric}`])
        .filter(value => value !== null && value !== undefined && Number.isFinite(value))
    ))
  );
  const rpmMax = Math.max(2000, roundUpTo(getMetricMax('rpm') * 1.1, 250));
  const rpmStep = rpmMax > 2500 ? 500 : 250;
  const rpmTicks = buildTicks(rpmMax, rpmStep);
  const fuelStep = flowLabel === 'gal/h' ? 5 : 10;
  const fuelMinMax = flowLabel === 'gal/h' ? 30 : 100;
  const totalFuelMax = Math.max(
    0,
    ...chartData
      .map(point => point.total_fuel)
      .filter(value => value !== null && value !== undefined && Number.isFinite(value))
  );
  const fuelMax = Math.max(fuelMinMax, roundUpTo(Math.max(getMetricMax('fuel'), totalFuelMax) * 1.15, fuelStep));
  const fuelTicks = buildTicks(fuelMax, fuelStep);
  const loadTicks = [0, 20, 40, 60, 80, 100];
  const temperatureTicks = isFahrenheit
    ? [32, 70, 110, 150, 190, 230, 270]
    : [0, 20, 40, 60, 80, 100, 120];
  const pressureStep = pressureLabel === 'psi' ? 5 : 0.25;
  const pressureMinMax = pressureLabel === 'psi' ? 30 : 2;
  const pressureMax = Math.max(pressureMinMax, roundUpTo(getMetricMax('boost') * 1.15, pressureStep));
  const pressureTicks = buildTicks(pressureMax, pressureStep);
  // Auto-escala: min/max reais de cada métrica para abrir a resolução do eixo Y.
  const getMetricRange = (metric) => {
    const values = chartData.flatMap(point => (
      ENGINE_SERIES
        .map(engine => point[`${engine.id}_${metric}`])
        .filter(value => value !== null && value !== undefined && Number.isFinite(value))
    ));
    if (!values.length) return null;
    return { min: Math.min(...values), max: Math.max(...values) };
  };
  const temperatureFallbackAxis = {
    domain: [temperatureTicks[0], temperatureTicks[temperatureTicks.length - 1]],
    ticks: temperatureTicks,
  };
  const temperatureMinStep = isFahrenheit ? 5 : 2;
  const tempAxis = buildNiceAxis(getMetricRange('temp'), {
    targetTicks: 6,
    padRatio: 0.08,
    minStep: temperatureMinStep,
  }) || temperatureFallbackAxis;
  const exhaustAxis = buildNiceAxis(getMetricRange('exhaust_temp'), {
    targetTicks: 6,
    padRatio: 0.08,
    minStep: temperatureMinStep * 2,
  }) || temperatureFallbackAxis;
  const boostAxis = buildNiceAxis(getMetricRange('boost'), {
    targetTicks: 6,
    padRatio: 0.1,
    minStep: pressureStep,
  }) || { domain: [0, pressureMax], ticks: pressureTicks };
  const rpmAxis = buildNiceAxis(getMetricRange('rpm'), {
    targetTicks: 6,
    padRatio: 0.08,
    minStep: 5,
  }) || { domain: [0, rpmMax], ticks: rpmTicks };
  const yAxisProps = {
    stroke: '#888',
    width: 72,
    tickMargin: 8,
    tickFormatter: formatAxisNumber,
  };
  // Eixo X por faixa de dia: hora (HH:MM) nos ticks regulares quando intradiário,
  // data (ex.: "qua 27/05") alinhada ao início de cada faixa logo após a virada de
  // dia, traço vertical em cada meia-noite e zebra de fundo alternando os dias.
  const timeDomain = chartData.length
    ? { min: chartData[0].time, max: chartData[chartData.length - 1].time }
    : null;
  const xSpanMs = timeDomain ? timeDomain.max - timeDomain.min : 0;
  const isIntraday = xSpanMs > 0 && xSpanMs <= 26 * 60 * 60 * 1000;
  const allMidnights = timeDomain
    ? getLocalMidnights(timeDomain.min, timeDomain.max, timezone)
    : [];
  // Divisores: todas as meia-noites; decima p/ no máx. ~8 faixas em janelas longas.
  let boundaries = allMidnights;
  if (allMidnights.length + 1 > 10) {
    const stride = Math.ceil((allMidnights.length + 1) / 8);
    boundaries = allMidnights.filter((_, i) => i % stride === 0);
  }
  // Faixas de dia: segmentos entre as bordas do domínio e os divisores.
  const bandEdges = timeDomain
    ? Array.from(new Set([timeDomain.min, ...boundaries, timeDomain.max])).sort((a, b) => a - b)
    : [];
  const dayBands = [];
  for (let i = 0; i < bandEdges.length - 1; i += 1) {
    const start = bandEdges[i];
    const end = bandEdges[i + 1];
    if (end > start) {
      dayBands.push({ start, end, label: formatDayLabel(start, timezone), index: dayBands.length });
    }
  }
  const xTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const hourTicks = (isIntraday && timeDomain)
    ? (buildTimeTicks(timeDomain.min, timeDomain.max, 6) || [])
    : [];
  // Rótulo de data só em faixas largas o bastante p/ o texto (evita slivers de borda).
  const MIN_DAY_LABEL_FRAC = 0.06;
  const dayLabelTicks = dayBands
    .filter(b => (b.end - b.start) >= xSpanMs * MIN_DAY_LABEL_FRAC)
    .map(b => b.start);
  const hourSet = new Set(hourTicks);
  const dayLabelByTs = {};
  dayBands.forEach(b => { dayLabelByTs[b.start] = b.label; });
  const xTicks = Array.from(new Set([...hourTicks, ...dayLabelTicks])).sort((a, b) => a - b);
  const tickMeta = {};
  for (const t of xTicks) {
    tickMeta[t] = {
      hour: hourSet.has(t) ? xTimeFormatter.format(t) : null,
      day: dayLabelByTs[t] != null && dayLabelTicks.includes(t) ? dayLabelByTs[t] : null,
    };
  }
  const hasHourRow = xTicks.some(t => tickMeta[t]?.hour);
  const hasDayRow = xTicks.some(t => tickMeta[t]?.day);
  const renderXAxisTick = ({ x, y, payload }) => {
    const meta = tickMeta[payload.value] || {};
    return (
      <g transform={`translate(${x},${y})`}>
        {meta.hour ? (
          <text x={0} y={0} dy={12} textAnchor="middle" fontSize={11} fill="#888">
            {meta.hour}
          </text>
        ) : null}
        {meta.day ? (
          <text
            x={5}
            y={0}
            dy={hasHourRow ? 26 : 14}
            textAnchor="start"
            fontSize={11}
            fontWeight={600}
            fill="#e0b057"
          >
            {meta.day}
          </text>
        ) : null}
      </g>
    );
  };
  // Zebra de fundo: faixas de dia alternadas (sutil, atrás das séries).
  const renderDayBands = () => dayBands.map((b, idx) => (
    idx % 2 === 0 ? (
      <ReferenceArea
        key={`day-band-${idx}`}
        x1={b.start}
        x2={b.end}
        fill="#8aa0bf"
        fillOpacity={0.055}
        ifOverflow="hidden"
      />
    ) : null
  ));
  // Traços verticais nas viradas de dia (reutilizado por todos os gráficos).
  const renderMidnightLines = () => boundaries.map((m, idx) => (
    <ReferenceLine
      key={`midnight-${idx}`}
      x={m}
      stroke="#5a6677"
      strokeDasharray="2 4"
      strokeWidth={1}
    />
  ));
  const xAxisProps = {
    dataKey: 'time',
    type: 'number',
    domain: ['dataMin', 'dataMax'],
    ticks: xTicks.length ? xTicks : undefined,
    tick: renderXAxisTick,
    interval: 0,
    height: (hasHourRow && hasDayRow) ? 42 : (hasDayRow ? 26 : 24),
    stroke: '#888',
    tickMargin: 8,
  };
  const renderEngineLines = (metric, options = {}) => ENGINE_SERIES.map(engine => (
    chartData.some(d => d[`${engine.id}_${metric}`] != null) && (
      <Line
        key={`${engine.id}-${metric}`}
        type="monotone"
        dataKey={`${engine.id}_${metric}`}
        stroke={engine.color}
        strokeDasharray={options.dashed ? '5 5' : undefined}
        name={options.nameSuffix ? `${engine.label} ${options.nameSuffix}` : engine.label}
        dot={false}
        // Telemetria de motor e um processo continuo: null = amostra ausente
        // (nao uma parada). Quando duas fontes fisicas dividem um mesmo path
        // (ex.: draga + bombordo durante incidente), a serie de um motor fica
        // esparsa na grade temporal compartilhada -> linha serrilhada se nao
        // conectarmos os nulls. Paradas reais sao zeros explicitos (renderizados);
        // periodos offline reais ja sao sombreados por ReferenceArea (dataGaps).
        connectNulls={true}
      />
    )
  ));
  const renderFuelLines = () => FUEL_SERIES.map(engine => {
    const hasData = chartData.some(d => d[`${engine.id}_fuel`] != null);
    const shouldShow = hasData || ALWAYS_SHOW_FUEL_ENGINE_IDS.includes(engine.id);
    if (!shouldShow) return null;

    return (
      <Line
        key={`${engine.id}-fuel`}
        type="monotone"
        dataKey={`${engine.id}_fuel`}
        stroke={engine.color}
        name={engine.label}
        dot={false}
        // Vide renderEngineLines: combustivel e processo continuo; null = amostra
        // ausente na grade compartilhada (nao parada). Conectar evita o serrilhado
        // da serie esparsa; paradas reais (0 L/h) seguem renderizadas.
        connectNulls={true}
      />
    );
  });
  const formatFuelTotal = (value) => (
    value !== null && value !== undefined && Number.isFinite(Number(value))
      ? Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })
      : '--'
  );
  const fuelTotalizerItems = FUEL_TOTALIZER_ITEMS.map(item => ({
    ...item,
    value: item.id === 'total'
      ? (fuelTotalsByMotor.total ?? totalFuelValue)
      : fuelTotalsByMotor[item.id],
  }));
  const visibleDataGaps = dataGaps.slice(0, MAX_GAP_REFERENCE_AREAS);
  const gridStyle = columns
    ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
    : undefined;

  // Loading state
  if (loading) {
    return (
      <div className="grid gap-4" style={gridStyle}>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[200px]" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Card className="bg-destructive/10">
        <CardContent className="p-6 text-center">
          <p className="text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }
  
  // No data state
  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            Nenhum dado de telemetria disponível para o período selecionado
          </p>
        </CardContent>
      </Card>
    );
  }
  
  // Tooltip comum
  const CustomTooltip = ({ active, payload, label, formatter }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-background border rounded-lg p-2 shadow-lg">
        <p className="text-xs text-muted-foreground mb-1">
          {formatDateTime(label, { omitSeconds: true })}
        </p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value?.toFixed(1)} {formatter?.(entry)}
          </p>
        ))}
      </div>
    );
  };
  
  return (
    <div className="grid gap-4" style={gridStyle}>
      {/* Gráfico de Velocidade */}
      {hasSpeed && isChartSelected('speed') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              Velocidade
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width={chartWidth} height={chartHeight}>
              <AreaChart data={chartData} syncId="telemetrySync">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis {...xAxisProps} />
                <YAxis stroke="#888" unit={` ${speedLabel}`} />
                <Tooltip content={<CustomTooltip formatter={() => speedLabel} />} />
                {renderDayBands()}
                {renderMidnightLines()}
                {visibleDataGaps.map((gap, idx) => (
                  <ReferenceArea
                    key={`gap-speed-${idx}`}
                    x1={gap.start}
                    x2={gap.end}
                    fill="#f59e0b"
                    fillOpacity={0.15}
                  />
                ))}
                <Area 
                  type="monotone" 
                  dataKey="speed_display" 
                  stroke={COLORS.speed}
                  fill={COLORS.speed}
                  fillOpacity={0.3}
                  name={`Velocidade (${speedLabel})`}
                  connectNulls={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      
      {/* Gráfico de RPM */}
      {hasRpm && isChartSelected('rpm') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              RPM dos Motores
              <span className="text-xs text-muted-foreground font-normal">(rpm)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width={chartWidth} height={chartHeight}>
              <LineChart data={chartData} syncId="telemetrySync">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis {...xAxisProps} />
                <YAxis
                  {...yAxisProps}
                  domain={rpmAxis.domain}
                  ticks={rpmAxis.ticks}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {renderDayBands()}
                {renderMidnightLines()}
                {visibleDataGaps.map((gap, idx) => (
                  <ReferenceArea
                    key={`gap-rpm-${idx}`}
                    x1={gap.start}
                    x2={gap.end}
                    fill="#f59e0b"
                    fillOpacity={0.15}
                  />
                ))}
                {renderEngineLines('rpm')}
                {/* D9 FIX: Linha de média BB+BE */}
                {chartData.some(d => d.avg_rpm != null) && (
                  <Line 
                    type="monotone" 
                    dataKey="avg_rpm" 
                    stroke={COLORS.avg}
                    strokeDasharray="5 5"
                    name="Média"
                    dot={false}
                    connectNulls={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Gráfico de Combustível */}
      {hasFuel && isChartSelected('fuel') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Fuel className="h-4 w-4" />
              Consumo de Combustível
              <span className="text-xs text-muted-foreground font-normal">({flowLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width={chartWidth} height={chartHeight}>
              <LineChart data={chartData} syncId="telemetrySync">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis {...xAxisProps} />
                <YAxis
                  {...yAxisProps}
                  domain={[0, fuelMax]}
                  ticks={fuelTicks}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {renderDayBands()}
                {renderMidnightLines()}
                {visibleDataGaps.map((gap, idx) => (
                  <ReferenceArea
                    key={`gap-fuel-${idx}`}
                    x1={gap.start}
                    x2={gap.end}
                    fill="#f59e0b"
                    fillOpacity={0.15}
                  />
                ))}
                {renderFuelLines()}
                {chartData.some(d => d.total_fuel != null) && (
                  <Line
                    type="monotone"
                    dataKey="total_fuel"
                    stroke={COLORS.totalFuel}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    name="Consumo Total"
                    dot={false}
                    connectNulls={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 border-t border-border pt-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Totalizador por motor
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {fuelTotalizerItems.map(item => (
                  <div
                    key={item.id}
                    className="rounded-md border border-border bg-muted/20 px-3 py-2"
                    style={{ borderLeft: `3px solid ${item.color}` }}
                  >
                    <p className="text-[11px] font-medium" style={{ color: item.color }}>
                      {item.label}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-foreground">
                      {formatFuelTotal(item.value)}
                      <span className="ml-1 text-xs text-muted-foreground">{totalFuelUnit}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gráfico de Boost */}
      {hasBoost && isChartSelected('boost') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              Pressão Turbo (Boost)
              <span className="text-xs text-muted-foreground font-normal">({pressureLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width={chartWidth} height={chartHeight}>
              <LineChart data={chartData} syncId="telemetrySync">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis {...xAxisProps} />
                <YAxis
                  {...yAxisProps}
                  domain={boostAxis.domain}
                  ticks={boostAxis.ticks}
                />
                <Tooltip content={<CustomTooltip formatter={() => pressureLabel} />} />
                <Legend />
                {renderDayBands()}
                {renderMidnightLines()}
                {visibleDataGaps.map((gap, idx) => (
                  <ReferenceArea
                    key={`gap-boost-${idx}`}
                    x1={gap.start}
                    x2={gap.end}
                    fill="#f59e0b"
                    fillOpacity={0.15}
                  />
                ))}
                {renderEngineLines('boost')}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      
      {/* Gráfico de Temperatura */}
      {hasTemp && isChartSelected('temperature') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Thermometer className="h-4 w-4" />
              Temperatura
              <span className="text-xs text-muted-foreground font-normal">({temperatureLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width={chartWidth} height={chartHeight}>
              <LineChart data={chartData} syncId="telemetrySync">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis {...xAxisProps} />
                <YAxis
                  {...yAxisProps}
                  domain={tempAxis.domain}
                  ticks={tempAxis.ticks}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {renderDayBands()}
                {renderMidnightLines()}
                {visibleDataGaps.map((gap, idx) => (
                  <ReferenceArea
                    key={`gap-temp-${idx}`}
                    x1={gap.start}
                    x2={gap.end}
                    fill="#f59e0b"
                    fillOpacity={0.15}
                  />
                ))}
                {renderEngineLines('temp')}
                {/* D9 FIX: Linha de média BB+BE */}
                {chartData.some(d => d.avg_temp != null) && (
                  <Line
                    type="monotone"
                    dataKey="avg_temp"
                    stroke={COLORS.avg}
                    strokeDasharray="5 5"
                    name="Média"
                    dot={false}
                    connectNulls={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Gráfico de Carga */}
      {hasLoad && isChartSelected('load') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              Carga dos Motores
              <span className="text-xs text-muted-foreground font-normal">(%)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width={chartWidth} height={chartHeight}>
              <LineChart data={chartData} syncId="telemetrySync">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis {...xAxisProps} />
                <YAxis
                  {...yAxisProps}
                  domain={[0, 100]}
                  ticks={loadTicks}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip formatter={() => '%'} />} />
                <Legend />
                {renderDayBands()}
                {renderMidnightLines()}
                {visibleDataGaps.map((gap, idx) => (
                  <ReferenceArea
                    key={`gap-load-${idx}`}
                    x1={gap.start}
                    x2={gap.end}
                    fill="#f59e0b"
                    fillOpacity={0.15}
                  />
                ))}
                {renderEngineLines('load')}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Gráfico de Temperatura de Escape / SCAP */}
      {hasExhaustTemp && isChartSelected('exhaustTemp') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Thermometer className="h-4 w-4" />
              Temperatura de Escape (SCAP)
              <span className="text-xs text-muted-foreground font-normal">({temperatureLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width={chartWidth} height={chartHeight}>
              <LineChart data={chartData} syncId="telemetrySync">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis {...xAxisProps} />
                <YAxis
                  {...yAxisProps}
                  domain={exhaustAxis.domain}
                  ticks={exhaustAxis.ticks}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip formatter={() => temperatureLabel} />} />
                <Legend />
                {renderDayBands()}
                {renderMidnightLines()}
                {visibleDataGaps.map((gap, idx) => (
                  <ReferenceArea
                    key={`gap-exhaust-temp-${idx}`}
                    x1={gap.start}
                    x2={gap.end}
                    fill="#f59e0b"
                    fillOpacity={0.15}
                  />
                ))}
                {renderEngineLines('exhaust_temp')}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      
      {/* Mensagem se não houver nenhum gráfico */}
      {!hasSpeed && !hasRpm && !hasLoad && !hasBoost && !hasFuel && !hasTemp && !hasExhaustTemp && (
        <Card>
          <CardContent className="p-12 text-center">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Dados de telemetria recebidos, mas sem valores válidos para exibição
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TelemetryCharts;
